import { createContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import type { User as AuthUser, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { User, CEFRLevel } from '@/types/database'
import { identifyUser, resetAnalytics, analytics, type AuthMethod } from '@/lib/analytics'
import { setUserContext, clearUserContext } from '@/lib/sentry'

interface ProfileUpdate {
  name?: string | null
  native_language?: string
  target_language?: string
  level?: CEFRLevel
}

export interface AuthContextType {
  authUser: AuthUser | null
  profile: User | null
  session: Session | null
  isLoading: boolean
  isAuthenticated: boolean
  needsOnboarding: boolean
  authError: Error | null
  retryAuth: () => void
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>
  signUpWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>
  signInWithGoogle: () => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  updateProfile: (updates: ProfileUpdate) => Promise<{ error: Error | null }>
  deleteAccount: () => Promise<{ error: Error | null }>
}

export const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null)
  const [profile, setProfile] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authError, setAuthError] = useState<Error | null>(null)
  const [authRetryCount, setAuthRetryCount] = useState(0)
  const pendingAuthMethodRef = useRef<{ method: AuthMethod; isSignUp: boolean } | null>(null)

  const fetchProfile = useCallback(async (userId: string, authUser?: AuthUser) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', userId)
      .single()

    if (error) {
      console.error('Error fetching profile:', error)
      return null
    }

    // Identify user for analytics and error tracking when profile is loaded
    if (data) {
      const profile = data as User
      setUserContext(userId)
      identifyUser(userId, {
        user_id: userId,
        name: profile.name,
        native_language: profile.native_language,
        target_language: profile.target_language,
        cefr_level: profile.level,
        subscription_tier: 'free',
        is_pro: false,
        created_at: profile.created_at,
      })

      // Track auth event if there's a pending auth method
      const pendingAuth = pendingAuthMethodRef.current
      const storedAuth = sessionStorage.getItem('pendingAuthMethod')

      if (pendingAuth) {
        if (pendingAuth.isSignUp) {
          analytics.userSignedUp(pendingAuth.method)
        } else {
          analytics.userSignedIn(pendingAuth.method)
        }
        pendingAuthMethodRef.current = null
      } else if (storedAuth) {
        // Magic link auth from redirect
        try {
          const parsed = JSON.parse(storedAuth) as { method: AuthMethod; isSignUp: boolean }
          if (parsed.isSignUp) {
            analytics.userSignedUp(parsed.method)
          } else {
            analytics.userSignedIn(parsed.method)
          }
        } catch {
          // Ignore parse errors
        }
        sessionStorage.removeItem('pendingAuthMethod')
      } else if (authUser) {
        // For OAuth redirects, determine method from provider
        const provider = authUser.app_metadata?.provider
        if (provider === 'google') {
          const isNewUser = authUser.created_at === authUser.last_sign_in_at
          if (isNewUser) {
            analytics.userSignedUp('google')
          } else {
            analytics.userSignedIn('google')
          }
        }
      }
    }

    return data
  }, [])

  useEffect(() => {
    let isMounted = true

    async function initializeAuth() {
      setAuthError(null)

      try {
        // Add timeout to prevent indefinite hanging during token refresh
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Connection timeout')), 10000)
        )

        const { data: { session: existingSession } } = await Promise.race([
          sessionPromise,
          timeoutPromise,
        ])

        if (!isMounted) return

        if (existingSession) {
          setSession(existingSession)
          setAuthUser(existingSession.user)
          const userProfile = await fetchProfile(existingSession.user.id, existingSession.user)
          if (isMounted) setProfile(userProfile)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        // Set error state - don't clear session, user might have valid refresh token
        if (isMounted) {
          setAuthError(error instanceof Error ? error : new Error('Auth failed'))
        }
      } finally {
        // CRITICAL: Always set loading to false
        if (isMounted) {
          setIsLoading(false)
        }
      }

      // Step 2: Listen for auth state changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (_event, newSession) => {
          if (!isMounted) return

          setSession(newSession)
          setAuthUser(newSession?.user ?? null)

          if (newSession?.user) {
            const userProfile = await fetchProfile(newSession.user.id, newSession.user)
            if (isMounted) setProfile(userProfile)
          } else {
            setProfile(null)
          }
        }
      )

      return () => {
        subscription.unsubscribe()
      }
    }

    const cleanupPromise = initializeAuth()

    return () => {
      isMounted = false
      cleanupPromise.then(cleanup => cleanup?.())
    }
  }, [fetchProfile, authRetryCount])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    pendingAuthMethodRef.current = { method: 'email', isSignUp: false }
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      pendingAuthMethodRef.current = null
    }
    return { error: error ? new Error(error.message) : null }
  }, [])

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    pendingAuthMethodRef.current = { method: 'email', isSignUp: true }
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) {
      pendingAuthMethodRef.current = null
    }
    return { error: error ? new Error(error.message) : null }
  }, [])

  const signInWithMagicLink = useCallback(async (email: string) => {
    // Store in sessionStorage since magic link redirects to a new page
    sessionStorage.setItem('pendingAuthMethod', JSON.stringify({ method: 'magic_link', isSignUp: true }))
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      },
    })
    if (error) {
      sessionStorage.removeItem('pendingAuthMethod')
    }
    return { error: error ? new Error(error.message) : null }
  }, [])

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    })
    return { error: error ? new Error(error.message) : null }
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    resetAnalytics()
    clearUserContext()
  }, [])

  const retryAuth = useCallback(() => {
    setIsLoading(true)
    setAuthError(null)
    setAuthRetryCount(count => count + 1)
  }, [])

  const deleteAccount = useCallback(async () => {
    const accessToken = session?.access_token
    if (!accessToken) {
      return { error: new Error('No active session') }
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        const data = await response.json()
        return { error: new Error(data.error || 'Failed to delete account') }
      }

      // Clean up local state
      resetAnalytics()
      clearUserContext()
      setAuthUser(null)
      setProfile(null)
      setSession(null)

      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Failed to delete account') }
    }
  }, [session?.access_token])

  const updateProfile = useCallback(async (updates: ProfileUpdate) => {
    if (!authUser?.id) {
      return { error: new Error('No authenticated user') }
    }

    // Use upsert to handle race condition between trigger-created profile and onboarding
    const profileData = {
      auth_id: authUser.id,
      email: authUser.email,
      name: updates.name ?? null,
      native_language: updates.native_language ?? 'en',
      target_language: updates.target_language ?? 'es',
      level: updates.level ?? 'A1',
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await supabase
      .from('users')
      .upsert(profileData as never, { onConflict: 'auth_id' })
      .select()
      .single()

    if (error) {
      if (error.code === '23503') {
        await supabase.auth.signOut()
        return { error: new Error('Session expired. Please sign in again.') }
      }
      return { error: new Error(error.message) }
    }

    if (data) {
      setProfile(data as User)
    }

    return { error: null }
  }, [authUser?.id, authUser?.email])

  const needsOnboarding = !profile || !profile.name

  const value: AuthContextType = {
    authUser,
    profile,
    session,
    isLoading,
    isAuthenticated: !!authUser,
    needsOnboarding,
    authError,
    retryAuth,
    signInWithEmail,
    signUpWithEmail,
    signInWithMagicLink,
    signInWithGoogle,
    signOut,
    updateProfile,
    deleteAccount,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
